import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { OverviewDisplayComponent } from '@products/overview/overview-display-component.interface';
import { OverviewComponentConfig } from '@products/overview/overview-component.config';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Logger } from '@utils/logger';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import {
  DISTRIBUTIONS_COLUMN_MAX_SIZE,
  FUND_INFO_COLUMN_MAX_SIZE,
  SummaryService,
} from '../services/summary.service';
import {
  FundInformationPanel,
  GridDiv,
  OverviewChargesInfo,
  OverviewData,
  OverviewState,
  ProductDetailConfiguration,
} from '@types';
import { TranslateService } from '@shared/translate/translate.service';
import { SiteConfigService } from '@services/site-config.service';
import { AppStateService } from '@services/app-state.service';
import { ProductDetailService } from '@products/product-detail-layout/product-detail.service';
import dayjs from 'dayjs';

const logger = Logger.getLogger('FundInformationComponent');
const YEAR_AGO = dayjs().subtract(1, 'years').format('YYYY-MM-DD');

@Component({
  selector: 'ft-fund-information',
  templateUrl: './fund-information.component.html',
  styleUrls: ['./fund-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundInformationComponent
  extends BaseProductDetailComponent
  implements OverviewDisplayComponent, OnDestroy, OnInit {
  componentConfiguration: OverviewComponentConfig;
  dataSubscription: Subscription;
  overviewData: OverviewData = null;
  gridDiv: GridDiv;
  chargesInfo: OverviewChargesInfo = null;
  isTemit: boolean;
  isBrazil: boolean;
  perfIncDateStd: string;

  fundInformation: FundInformationPanel;
  salesCharges: FundInformationPanel;
  identifiers: FundInformationPanel;
  distributions: FundInformationPanel;
  tradingCharacteristics: FundInformationPanel;
  temitKeyDateDetails: FundInformationPanel;
  hideDividendYields = false;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private summaryService: SummaryService,
    private translateService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private siteConfig: SiteConfigService,
    private appStateService: AppStateService,
    private productDetailService: ProductDetailService
  ) {
    super();
    this.isTemit = this.appStateService.getChannel() === 'temit';
  }

  ngOnInit(): void {
    // WDE-11: Hide Dividend & Yields sections for not loggedin users in VIO
    this.productDetailService.isVIPNonLoggedIn$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((status) => {
        this.hideDividendYields = status;
      });
  }

  populate(
    componentConfig: OverviewComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    this.changeDetector.detectChanges();
    if (this.brConfig.fundId) {
      this.summaryService.setConfigurationName(
        componentConfig.configurationName
      );
      this.dataSubscription = this.summaryService.overviewState$.subscribe(
        (newState: OverviewState) => this.mapState(newState)
      );
      this.summaryService.populate(brConfig);
    }
  }

  /**
   * Called when state changes.
   * @param overviewState the new state returned by summary details service
   */
  mapState(overviewState: OverviewState) {
    this.chargesInfo = overviewState.chargesInfo;
    this.overviewData = overviewState.data;
    this.gridDiv = overviewState.gridDiv;
    this.isBrazil = overviewState.isBrazil;
    this.perfIncDateStd = overviewState.perfIncDateStd;

    this.loadPanelData();

    this.changeDetector.detectChanges();
  }

  /**
   * Loads Panel Data into their respective variables.
   */
  private loadPanelData() {
    this.fundInformation = this.fetchPanelData('fundInformation');
    this.salesCharges = this.fetchPanelData('salesCharges');
    this.identifiers = this.fetchPanelData('identifiers');
    this.distributions = this.fetchPanelData('distributions');
    this.tradingCharacteristics = this.fetchPanelData('tradingCharacteristics');
    this.temitKeyDateDetails = this.fetchPanelData('temitKeyDateDetails');
  }

  /**
   * Organize panel data in form of FundInformationPanel structure.
   * @param section field name as per overview data.
   */
  fetchPanelData(section: string): FundInformationPanel {
    let fundInfoPanel: FundInformationPanel;

    switch (section) {
      case 'fundInformation': {
        fundInfoPanel = {
          asOfDate: this.overviewData.fundInformation.asOfDate,
          updateFrequency: this.overviewData.fundInformation.updateFrequency,
          title:
            this.overviewData.fundInformation.items[0]?.section.trim() === ''
              ? this.translateService.instant(
                  'products.fund-info-section-fundInformation',
                  this.brConfig?.fundId
                ) // fallback scenario to display section std, if section name is blank.
              : this.overviewData.fundInformation.items[0]?.section,
          footnote: 'Fund Information:overview-fund-info-section',
          tooltip: this.translateService.tooltip(
            'products.overview-fund-info-section-tooltip'
          ),
          analyticsName: 'overview-fund-info-section',
          items: this.overviewData.fundInformation.items,
          twoColumn:
            this.overviewData.fundInformation.items.length >
            FUND_INFO_COLUMN_MAX_SIZE,
        };
        break;
      }
      case 'salesCharges': {
        fundInfoPanel = {
          asOfDate: this.overviewData.salesCharges.asOfDate,
          title:
            this.overviewData.salesCharges.items[0]?.section.trim() === ''
              ? this.translateService.instant(
                  'products.fund-info-section-salesCharges',
                  this.brConfig?.fundId
                )
              : this.overviewData.salesCharges.items[0]?.section,
          footnote: 'Fund Information:overview-sales-charges-section',
          tooltip: this.translateService.tooltip(
            'products.overview-sales-charges-section-tooltip'
          ),
          analyticsName: 'overview-sales-charges-section',
          items: this.overviewData.salesCharges.items,
          twoColumn: false,
          isMifidApplicable: this.perfIncDateStd >= YEAR_AGO,
        };

        if (this.siteConfig.isSiteInternational()) {
          fundInfoPanel.updateFrequency = this.overviewData.salesCharges.updateFrequency;
        }

        break;
      }
      case 'identifiers': {
        fundInfoPanel = {
          asOfDate: this.overviewData.identifiers.asOfDate,
          updateFrequency: this.overviewData.identifiers.updateFrequency,
          title:
            this.overviewData.identifiers.items[0]?.section.trim() === ''
              ? this.translateService.instant(
                  'products.fund-info-section-identifiers',
                  this.brConfig?.fundId
                )
              : this.overviewData.identifiers.items[0]?.section,
          footnote: 'Fund Information:overview-identifiers-section',
          tooltip: this.translateService.tooltip(
            'products.overview-identifiers-section-tooltip'
          ),
          analyticsName: 'overview-identifiers-section',
          items: this.overviewData.identifiers.items,
          twoColumn: false,
        };
        break;
      }
      case 'distributions': {
        fundInfoPanel = {
          asOfDate: this.overviewData.distributions.asOfDate,
          updateFrequency: this.overviewData.distributions.updateFrequency,
          title:
            this.overviewData.distributions.items[0]?.section.trim() === ''
              ? this.translateService.instant(
                  'products.fund-info-section-distributions',
                  this.brConfig?.fundId
                )
              : this.overviewData.distributions.items[0]?.section,
          footnote: 'Fund Information:overview-distributions-section',
          tooltip: this.translateService.tooltip(
            'products.overview-distributions-section-tooltip'
          ),
          analyticsName: 'overview-distributions-section',
          items: this.overviewData.distributions.items,
          twoColumn:
            this.overviewData.distributions.items.length >
            DISTRIBUTIONS_COLUMN_MAX_SIZE,
        };
        break;
      }
      case 'tradingCharacteristics': {
        fundInfoPanel = {
          asOfDate: this.overviewData.tradingCharacteristics.asOfDate,
          updateFrequency: this.overviewData.tradingCharacteristics
            .updateFrequency,
          title:
            this.overviewData.tradingCharacteristics.items[0]?.section.trim() ===
            ''
              ? this.translateService.instant(
                  'products.fund-info-section-tradingCharacteristics',
                  this.brConfig?.fundId
                )
              : this.overviewData.tradingCharacteristics.items[0]?.section,
          footnote: 'Fund Information:overview-trading-characteristics-section',
          tooltip: this.translateService.tooltip(
            'products.overview-trading-characteristics-section-tooltip'
          ),
          analyticsName: 'overview-trading-characteristics-section',
          items: this.overviewData.tradingCharacteristics.items,
          twoColumn: false,
        };
        break;
      }
      case 'temitKeyDateDetails': {
        fundInfoPanel = {
          asOfDate: '',
          title: this.translateService.instant(
            'products.fund-info-section-temitKeyDateDetails'
          ),
          footnote: '',
          tooltip: '',
          analyticsName: 'overview-temitKeyDateDetails-section',
          items: this.overviewData.temitKeyDateDetails.items,
          twoColumn: false,
        };
        break;
      }
    }

    return fundInfoPanel;
  }

  /**
   * Split the items array into two.
   * @param detail section wise summary detail
   */

  ngOnDestroy() {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
